import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useMode } from '../contexts/ModeContext';
import Navbar from '../components/Navbar';
import LanguageToggle from '../components/LanguageToggle';
import ModeToggle from '../components/ModeToggle';
import ProgressChart from '../components/ProgressChart';
import AchievementBadges from '../components/AchievementBadges';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudentDashboard = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const { mode } = useMode();
  const navigate = useNavigate();
  
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      if (user.role === 'student') {
        fetchStudentProgress();
      } else {
        // Redirect to appropriate dashboard based on role
        if (user.role === 'admin') navigate('/admin/dashboard');
        else if (user.role === 'teacher') navigate('/teacher/dashboard');
        else if (user.role === 'parent') navigate('/parent/dashboard');
      }
    }
  }, [user, navigate]);

  const fetchStudentProgress = async () => {
    try {
      const res = await axios.get(`${API}/personalization/progress`, { withCredentials: true });
      setProgress(res.data);
    } catch (error) {
      console.error('Error fetching progress:', error);
      setProgress({
        total_questions_attempted: 0,
        overall_accuracy: 0,
        recommended_topics: []
      });
    } finally {
      setLoading(false);
    }
  };

  const studentSections = [
    {
      id: 'personalized',
      icon: '🎯',
      title: language === 'tr' ? 'Kişiselleştirilmiş Öğrenme' : 'Personalized Learning',
      description: language === 'tr' ? 'Haftalık kitapçıklar ve ilerleme takibi' : 'Weekly booklets and progress tracking',
      path: '/personalized-learning',
      gradient: 'from-blue-500 to-cyan-500',
      highlight: true
    },
    {
      id: 'my-plan',
      icon: '📅',
      title: language === 'tr' ? 'Planım' : 'My Plan',
      description: language === 'tr' ? 'Haftalık çalışma programını gör' : 'View your weekly study schedule',
      path: '/my-plan',
      gradient: 'from-purple-500 to-pink-500',
      highlight: false
    },
    {
      id: 'learning-journey',
      icon: '🎓',
      title: language === 'tr' ? 'Öğrenme Yolculuğum' : 'My Learning Journey',
      description: language === 'tr' ? 'İlerlemeni ve başarılarını takip et' : 'Track your progress and achievements',
      path: '/my-learning-journey',
      gradient: 'from-emerald-500 to-teal-500',
      highlight: false
    },
    {
      id: 'my-tasks',
      icon: '✅',
      title: language === 'tr' ? 'Görevlerim' : 'My Tasks',
      description: language === 'tr' ? 'Geçmiş ve devam eden görevler' : 'Past and ongoing tasks',
      path: '/my-tasks',
      gradient: 'from-orange-500 to-red-500',
      highlight: false
    },
    {
      id: 'chat',
      icon: '🤖',
      title: language === 'tr' ? 'AI Mentor' : 'AI Mentor',
      description: language === 'tr' ? 'Yapay zeka mentor ile öğren' : 'Learn with AI mentor',
      path: '/chat/general',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      id: 'evaluator',
      icon: '✍️',
      title: language === 'tr' ? 'Cevap Değerlendirici' : 'Answer Evaluator',
      description: language === 'tr' ? 'Cevaplarını AI ile değerlendir' : 'Evaluate answers with AI',
      path: '/evaluator',
      gradient: 'from-green-500 to-teal-500'
    },
    {
      id: 'question-bank',
      icon: '📚',
      title: language === 'tr' ? 'Soru Bankası' : 'Question Bank',
      description: language === 'tr' ? 'Tüm soruları incele ve çöz' : 'Browse and solve all questions',
      path: '/question-bank',
      gradient: 'from-orange-500 to-red-500'
    },
    {
      id: 'booklet-designer',
      icon: '📝',
      title: language === 'tr' ? 'Kitapçık Tasarla' : 'Design Booklet',
      description: language === 'tr' ? 'Özel soru kitapçığı oluştur' : 'Create custom question booklet',
      path: '/booklet-designer',
      gradient: 'from-indigo-500 to-purple-500'
    },
    {
      id: 'study-planner',
      icon: '📅',
      title: language === 'tr' ? 'Çalışma Planlayıcı' : 'Study Planner',
      description: language === 'tr' ? 'Çalışma programını planla' : 'Plan your study schedule',
      path: '/study-plan',
      gradient: 'from-pink-500 to-rose-500'
    },
    {
      id: 'premium-detailed-plan',
      icon: '✨',
      title: language === 'tr' ? 'Premium Haftalık Plan' : 'Premium Weekly Plan',
      description: language === 'tr' ? 'AI destekli detaylı günlük çalışma planı' : 'AI-powered detailed daily study plan',
      path: '/premium/detailed-plan',
      gradient: 'from-indigo-600 to-purple-600',
      highlight: true
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{language === 'tr' ? 'Yükleniyor...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  const modeColors = mode === 'AP' 
    ? 'from-blue-600 via-indigo-600 to-purple-600'
    : 'from-red-600 via-orange-600 to-yellow-500';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <LanguageToggle />
      <ModeToggle />
      <Navbar title={language === 'tr' ? 'Öğrenci Paneli' : 'Student Dashboard'} />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className={`bg-gradient-to-r ${modeColors} rounded-2xl shadow-2xl p-8 mb-8 text-white`}>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2">
                🎓 {language === 'tr' ? 'Hoş Geldin' : 'Welcome'}, {user?.name?.split(' ')[0] || 'Student'}!
              </h1>
              <p className="text-blue-100 text-lg">
                {language === 'tr' 
                  ? `${mode} sınav modunda öğrenmeye hazır mısın?`
                  : `Ready to learn in ${mode} exam mode?`}
              </p>
            </div>
            <div className="text-6xl">{mode === 'AP' ? '🌍' : '🇹🇷'}</div>
          </div>
        </div>

        {/* Progress Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-3xl">📝</span>
              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                {language === 'tr' ? 'Toplam' : 'Total'}
              </span>
            </div>
            <div className="text-3xl font-bold text-gray-800 mb-1">{progress?.total_questions_attempted || 0}</div>
            <div className="text-sm text-gray-600">{language === 'tr' ? 'Çözülen Soru' : 'Questions Solved'}</div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-3xl">🎯</span>
              <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                {language === 'tr' ? 'Başarı' : 'Accuracy'}
              </span>
            </div>
            <div className="text-3xl font-bold text-gray-800 mb-1">{progress?.overall_accuracy?.toFixed(1) || 0}%</div>
            <div className="text-sm text-gray-600">{language === 'tr' ? 'Doğruluk Oranı' : 'Success Rate'}</div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-3xl">⚡</span>
              <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-semibold">
                {language === 'tr' ? 'Hedef' : 'Goal'}
              </span>
            </div>
            <div className="text-3xl font-bold text-gray-800 mb-1">0</div>
            <div className="text-sm text-gray-600">{language === 'tr' ? 'Günlük Seri' : 'Day Streak'}</div>
          </div>
        </div>

        {/* Progress Visualization & Achievements */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <ProgressChart
            data={[
              { label: language === 'tr' ? 'Matematik' : 'Mathematics', value: progress?.topic_proficiencies?.['Mathematics']?.accuracy || 0 },
              { label: language === 'tr' ? 'Fizik' : 'Physics', value: progress?.topic_proficiencies?.['Physics']?.accuracy || 0 },
              { label: language === 'tr' ? 'Kimya' : 'Chemistry', value: progress?.topic_proficiencies?.['Chemistry']?.accuracy || 0 },
              { label: language === 'tr' ? 'Biyoloji' : 'Biology', value: progress?.topic_proficiencies?.['Biology']?.accuracy || 0 }
            ]}
            title={language === 'tr' ? '📊 Konu Başarı Oranları' : '📊 Topic Success Rates'}
            language={language}
          />
          <AchievementBadges
            achievements={{
              first_study: (progress?.total_questions_attempted || 0) > 0,
              week_streak: false,
              perfect_score: (progress?.overall_accuracy || 0) >= 90,
              fast_learner: (progress?.total_questions_attempted || 0) >= 50,
              bookworm: false,
              rising_star: (progress?.overall_accuracy || 0) >= 70
            }}
            language={language}
          />
        </div>

        {/* Student Sections */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            {language === 'tr' ? '🚀 Öğrenmeye Başla' : '🚀 Start Learning'}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {studentSections.map((section) => (
              <div
                key={section.id}
                onClick={() => navigate(section.path)}
                className={`group bg-white rounded-2xl shadow-lg p-6 cursor-pointer hover:shadow-2xl transform hover:scale-105 transition-all duration-300 ${
                  section.highlight ? 'ring-2 ring-blue-500' : ''
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`inline-flex p-4 bg-gradient-to-br ${section.gradient} rounded-2xl text-4xl shadow-lg`}>
                    {section.icon}
                  </div>
                  {section.highlight && (
                    <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-full px-3 py-1 font-bold text-xs">
                      {language === 'tr' ? 'ÖNERİLEN' : 'RECOMMENDED'}
                    </div>
                  )}
                </div>
                
                <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-blue-600 transition-colors">
                  {section.title}
                </h3>
                
                <p className="text-sm text-gray-600 mb-4">
                  {section.description}
                </p>
                
                <div className="flex items-center text-blue-600 font-semibold">
                  <span>{language === 'tr' ? 'Başla' : 'Start'}</span>
                  <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recommended Topics */}
        {progress?.recommended_topics?.length > 0 && (
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl p-6 border border-yellow-200">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
              💡 {language === 'tr' ? 'Senin İçin Önerilen Konular' : 'Recommended Topics for You'}
            </h3>
            <div className="flex flex-wrap gap-2">
              {progress.recommended_topics.map((topic, idx) => (
                <span key={idx} className="bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full text-sm font-semibold">
                  {topic}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentDashboard;
